import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { DominoLogo } from '@/components/ui/DominoLogo';
import { signupPrimary } from '@/components/signup/SignupParts';

type Failure = { title: string; detail: string; retry: boolean };

const failureOf = (message: string, details: string | null): Failure => {
  if (message === 'company_domain_taken') {
    return { title: 'Ce diagnostic existe déjà', detail: `Un diagnostic existe déjà pour ${details ?? 'ce site'}. Demandez à la personne qui l’a lancé de vous inviter.`, retry: false };
  }
  if (message === 'company_email_personal') {
    return { title: 'Utilisez votre email professionnel', detail: 'Le diagnostic porte sur votre entreprise : créez votre compte avec votre adresse professionnelle.', retry: false };
  }
  if (message === 'company_signup_invalid') {
    return { title: 'Entreprise incomplète', detail: 'Le nom ou le site de l’entreprise manque. Recommencez l’inscription.', retry: false };
  }
  return { title: 'Domino n’a pas pu préparer votre espace', detail: 'Réessayez dans un instant.', retry: true };
};

/**
 * First opening after signup (19/10/2026): records the company typed at signup
 * for this workspace (`claim_my_company`, one diagnostic per website domain),
 * then opens the Profil where the analyst starts on its own.
 */
export const WelcomePage = () => {
  const navigate = useNavigate();
  const [failure, setFailure] = useState<Failure | null>(null);
  const claimed = useRef(false);

  const claim = useCallback(async () => {
    setFailure(null);
    const { error } = await supabase.rpc('claim_my_company');
    if (!error) { navigate('/profil?analyse=1', { replace: true }); return; }
    // An account without signup data (an invited person, an older account) just opens Domino.
    if (error.message === 'company_signup_missing') { navigate('/', { replace: true }); return; }
    setFailure(failureOf(error.message, error.details || null));
  }, [navigate]);

  useEffect(() => {
    if (claimed.current) return;
    claimed.current = true;
    void claim();
  }, [claim]);

  if (!failure) {
    return (
      <div className="flex h-screen items-center justify-center bg-domino-ivory">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-domino-border border-t-domino-aubergine" />
      </div>
    );
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-domino-ivory px-6">
      <div className="flex max-w-md flex-col items-center gap-4 text-center">
        <DominoLogo tile className="h-10 w-10" />
        <h1 className="font-display text-2xl font-semibold text-domino-aubergine">{failure.title}</h1>
        <p className="text-domino-inkSoft">{failure.detail}</p>
        {failure.retry ? (
          <button type="button" className={signupPrimary} onClick={() => void claim()}>Réessayer</button>
        ) : (
          <button type="button" className={signupPrimary} onClick={() => void supabase.auth.signOut()}>Se déconnecter</button>
        )}
      </div>
    </main>
  );
};
